import React, { useCallback, useId, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUploadFile } from '../hooks/useFiles';
import { Card } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Progress } from '../../../components/ui/progress';
import { cn } from '../../../lib/utils';

export interface FileUploadResult {
  id: string;
  original_filename: string;
  mime_type: string;
}

interface FileUploadProps {
  /** Entity reference for attachments in "table_name:uuid" format */
  attachedTo?: string;
  folderId?: string | null;
  onUploadComplete?: (fileId: string, metadata?: FileUploadResult) => void | Promise<void>;
  maxSizeMB?: number;
  accept?: string;
  isPublic?: boolean;
  /** Whether file is part of user's file management (default: true) */
  isUserFile?: boolean;
  /** Compact mode for inline/embedded use */
  compact?: boolean;
  /** Allow selecting multiple files at once */
  multiple?: boolean;
  /** Multi-company (S-pattern): owning company id. null = shared. */
  companyId?: string | null;
}

export function FileUpload({
  attachedTo,
  folderId,
  onUploadComplete,
  maxSizeMB = 50,
  accept,
  isPublic = false,
  isUserFile = true,
  compact = false,
  multiple = false,
  companyId,
}: FileUploadProps) {
  const { t } = useTranslation('file_management');
  const uploadMutation = useUploadFile();
  const inputId = useId();

  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        return t('maxSizeError', { size: maxSizeMB });
      }
      if (file.size === 0) {
        return t('emptyFileError');
      }
      return null;
    },
    [maxSizeMB, t]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);

      try {
        const result = await uploadMutation.mutateAsync({
          file,
          folder_id: folderId || undefined,
          is_public: isPublic,
          is_user_file: isUserFile,
          attached_to: attachedTo,
          company_id: companyId ?? null,
        });
        const data = result.data;
        await onUploadComplete?.(data.id, {
          id: data.id,
          original_filename: data.original_filename,
          mime_type: data.mime_type,
        });
      } catch (err: unknown) {
        const apiError = err as { error?: { message?: string } };
        const message = apiError?.error?.message || t('uploadError');
        setError(message);
        console.error('Upload failed:', err);
      }
    },
    [validateFile, attachedTo, folderId, isPublic, isUserFile, companyId, uploadMutation, onUploadComplete, t]
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        await uploadFile(file);
      }
    },
    [uploadFile]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        if (multiple) {
          uploadFiles(files);
        } else {
          uploadFile(files[0]);
        }
      }
    },
    [uploadFile, uploadFiles, multiple]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        // Reset input value immediately so the same file can be re-selected
        e.target.value = '';
        if (multiple) {
          uploadFiles(fileArray);
        } else {
          uploadFile(fileArray[0]);
        }
      }
    },
    [uploadFile, uploadFiles, multiple]
  );

  const isUploading = uploadMutation.isPending;

  return (
    <div onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <Card
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !isUploading && document.getElementById(inputId)?.click()}
        className={cn(
          'text-center border-dashed transition-colors',
          compact ? 'p-3 border' : 'p-6 border-2',
          isUploading
            ? 'cursor-wait opacity-70'
            : 'cursor-pointer hover:border-primary hover:bg-accent',
          dragActive && 'border-primary bg-accent'
        )}
      >
        <input
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          className="hidden"
          disabled={isUploading}
        />
        {isUploading ? (
          <div className={cn('flex items-center justify-center', compact ? 'gap-2' : 'flex-col')}>
            <Loader2 className={cn('text-primary animate-spin', compact ? 'h-5 w-5' : 'h-10 w-10 mb-2')} />
            <p className={cn('text-muted-foreground', compact && 'text-sm')}>{t('uploading')}</p>
          </div>
        ) : (
          <div className={cn('flex items-center justify-center', compact ? 'gap-2' : 'flex-col')}>
            <Upload className={cn('text-muted-foreground', compact ? 'h-5 w-5' : 'h-10 w-10 mb-2')} />
            <p className={cn('text-muted-foreground', compact && 'text-sm')}>{t('dragDropHint')}</p>
          </div>
        )}
      </Card>

      {isUploading && (
        <Progress className="mt-2" value={undefined} aria-label={t('uploadProgress')} />
      )}

      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
